import { AxiosResponse } from 'axios';
import { useAxios } from '@/features/common/helpers/axios';
import { fileFormatType, InvoiceItemType } from '@/features/invoices/types';
import { getTokenAsQuery } from '@/features/auth/helpers';
import { parseError } from '@/features/common/helpers/errorParser';
import { GlobalResponseType } from '@/features/common/types';
import i18n from '@/i18n';

const { axiosService, baseURL } = useAxios();

export interface UrlQuery {
  offset?: number,
  limit?: number,
  orderBy?: string,
  orderByDesc?: boolean,
  search?: string | null,
  dateRange?: string[],
}

export const getInvoices = async ({
  offset = 0,
  limit = 30,
  orderBy = 'invoiceDate',
  orderByDesc = true,
  search = null,
  dateRange = [],
}: UrlQuery = {}): Promise<{ data?: InvoiceItemType[] } & GlobalResponseType> => {
  try {
    let queryString = `?Offset=${offset}&Limit=${limit}&OrderBy=${orderBy}&OrderByDescending=${orderByDesc}`;
    if (search) {
      queryString += `&search=${encodeURI(search)}`;
    }
    if (dateRange.length) {
      const [fromDate, toDate] = dateRange;
      if (fromDate) {
        queryString += `&fromDate=${fromDate}`;
      }
      if (toDate) {
        queryString += `&toDate=${toDate}`;
      }
    }

    const invoicesRes: AxiosResponse = await axiosService.get(
      `/invoices${queryString}`,
    );

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      data: invoicesRes.data,
    };
  } catch (err) {
    return parseError(err);
  }
};

export const getInvoicePdfSrc = async (
  invoiceNumber: string,
): Promise<string> => {
  const token = await getTokenAsQuery(axiosService);
  return `${baseURL}/invoices/${invoiceNumber}/print?bearerToken=${encodeURIComponent(token)}`;
};

export const exportInvoiceSrc = async (
  invoiceNumber: string,
  fileFormat: fileFormatType,
): Promise<string> => {
  const token = await getTokenAsQuery(axiosService);
  return `${baseURL}/invoices/${invoiceNumber}/download?fileFormat=${fileFormat}&bearerToken=${encodeURIComponent(token)}`;
};

const parseErrorMessageForInvoice = (err): GlobalResponseType => {
  const error = parseError(err);

  let errorMessage = '';
  switch (error.errorType) {
    case 'AccountPermissionException':
      errorMessage = i18n.t('invoices.errorMessages.AccountPermissionException') as string;
      break;
    case 'InvoiceNotAvailableException':
      errorMessage = i18n.t('invoices.errorMessages.InvoiceNotAvailableException') as string;
      break;
    case 'FileNotFoundException':
      errorMessage = i18n.t('invoices.errorMessages.FileNotFoundException') as string;
      break;
    case 'UnknownException':
      errorMessage = i18n.t('invoices.errorMessages.UnknownException') as string;
      break;
    case 'ArgumentException':
      errorMessage = error.errorMessage;
      break;
    default:
      errorMessage = i18n.t('alertDialog.somethingWentWrong') as string;
  }

  return {
    ...error,
    errorMessage,
  };
};

export const getInvoiceAsPDF = async (
  invoiceNumber: string,
): Promise<{ data?: unknown, url?: string } & GlobalResponseType> => {
  try {
    const token = await getTokenAsQuery(axiosService);
    const response: AxiosResponse = await axiosService.get(
      `/invoices/${invoiceNumber}/print?bearerToken=${encodeURIComponent(token)}`,
    );

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      data: response.data,
      url: `${baseURL}/invoices/${invoiceNumber}/print?bearerToken=${encodeURIComponent(token)}`,
    };
  } catch (err) {
    return parseErrorMessageForInvoice(err);
  }
};

export const getInvoiceAsCSVOrExcel = async (
  invoiceNumber: string,
  fileFormat: fileFormatType,
): Promise<{ data?: unknown, url?: string } & GlobalResponseType> => {
  try {
    const token = await getTokenAsQuery(axiosService);
    const response: AxiosResponse = await axiosService.get(
      `/invoices/${invoiceNumber}/download?fileFormat=${fileFormat}&bearerToken=${encodeURIComponent(token)}`,
    );

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      data: response.data,
      url: `${baseURL}/invoices/${invoiceNumber}/download?fileFormat=${fileFormat}&bearerToken=${encodeURIComponent(token)}`,
    };
  } catch (err) {
    return parseErrorMessageForInvoice(err);
  }
};
